import { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import Actions from './Actions';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const VehicleTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [vehicles, setVehicles] = useState([]);

    const { user } = useSelector((state) => state.user);
    const isSuperAdmin = user.role === "admin";
    const perms = user.adminRole?.permissions?.vehicles || {};
    const canCreate = isSuperAdmin || perms.create;
    const canEdit = isSuperAdmin || perms.edit;
    const canDelete = isSuperAdmin || perms.delete;

    const fetchVehicles = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/vehicles');
            setVehicles(data.vehicles);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load vehicles", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVehicles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteHandler = async (id) => {
        try {
            await axios.delete(`/api/v1/admin/vehicle/${id}`);
            enqueueSnackbar("Vehicle removed", { variant: "success" });
            setVehicles((prev) => prev.filter((v) => v._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to remove vehicle", { variant: "error" });
        }
    };

    const columns = [
        {
            field: "vehicleNumber",
            headerName: "Vehicle Number",
            minWidth: 180,
            flex: 0.5,
        },
        {
            field: "type",
            headerName: "Type",
            minWidth: 140,
            flex: 0.4,
        },
        {
            field: "active",
            headerName: "Active",
            minWidth: 100,
            flex: 0.3,
            renderCell: (params) => (
                params.row.active
                    ? <span className="text-green-700 bg-green-100 px-2 py-0.5 rounded-full text-xs font-medium">Active</span>
                    : <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full text-xs font-medium">Inactive</span>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 100,
            flex: 0.3,
            sortable: false,
            renderCell: (params) => (
                <Actions editRoute={"vehicle"} deleteHandler={deleteHandler} id={params.row.id} name={params.row.vehicleNumber} canEdit={canEdit} canDelete={canDelete} />
            ),
        },
    ];

    const rows = vehicles.map((v) => ({
        id: v._id,
        vehicleNumber: v.vehicleNumber,
        type: v.type || "—",
        active: v.active,
    }));

    return (
        <>
            <MetaData title="Admin: Vehicles | 1313 Mart" />
            {loading && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <h1 className="text-lg font-medium uppercase">Vehicles</h1>
                {canCreate && (
                    <Link to="/admin/vehicle/new" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New Vehicle</Link>
                )}
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full" style={{ height: 500 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableSelectIconOnClick
                    sx={{ boxShadow: 0, border: 0 }}
                />
            </div>
        </>
    );
};

export default VehicleTable;